import { useSuspenseQuery } from '@tanstack/react-query';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { fetchFunds } from 'api/funds';
import { SelectCampaign } from 'assets/js/pages/pledges/SelectCampaign';
import { PledgeFormValue } from 'assets/js/pages/pledges/usePledgeForm';
import * as Form from 'components/form';
import { InputCurrencyIcon } from 'components/input';
import * as Select from 'components/select';
import { FUNDS_PARAMS } from 'constants/params';
import { InstallmentFrequency, PledgeType } from 'constants/pledges';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';

const PledgeBasicInfoForm = () => {
    const { accountId } = useCurrentAccount();
    const methods = Form.useFormContext<PledgeFormValue>();
    const { control, path, watch } = methods;

    const { data: fundsData } = useSuspenseQuery({
        queryKey: accountQueryKeys.fundsWithFilters(accountId, FUNDS_PARAMS),
        queryFn: () => fetchFunds(accountId, FUNDS_PARAMS),
    });
    const funds = fundsData.data;

    const [installmentFrequency, memo, internalNote] = watch([
        'installment_frequency',
        'memo',
        'internal_note',
    ]);

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                    <Form.Container>
                        <Form.Label name={path('amount')}>
                            Pledge amount <Form.LabelAsterisk />
                        </Form.Label>
                        <Form.TextInputRoot
                            control={control}
                            name="amount"
                            inputRootProps={{
                                mask: 'decimal',
                            }}
                            rules={{
                                required: 'Pledge amount is required',
                            }}
                        >
                            <InputCurrencyIcon />
                            <Form.TextInputForm />
                        </Form.TextInputRoot>
                        <Form.Error name={path('amount')} />
                    </Form.Container>
                    <Form.Container>
                        <Form.Label name={path('installment_frequency')}>
                            Installment frequency
                        </Form.Label>
                        <Form.SingleSelect control={control} name="installment_frequency">
                            <SingleSelect.Trigger aria-label="Installment frequency" />
                            <SingleSelect.Content>
                                <SingleSelect.Item value={InstallmentFrequency.OneTime}>
                                    <SingleSelect.ItemText>One-time</SingleSelect.ItemText>
                                </SingleSelect.Item>
                                <SingleSelect.Item value={InstallmentFrequency.Weekly}>
                                    <SingleSelect.ItemText>Weekly</SingleSelect.ItemText>
                                </SingleSelect.Item>
                                <SingleSelect.Item value={InstallmentFrequency.Monthly}>
                                    <SingleSelect.ItemText>Monthly</SingleSelect.ItemText>
                                </SingleSelect.Item>
                                <SingleSelect.Item value={InstallmentFrequency.Quarterly}>
                                    <SingleSelect.ItemText>Quarterly</SingleSelect.ItemText>
                                </SingleSelect.Item>
                                <SingleSelect.Item value={InstallmentFrequency.SemiAnnually}>
                                    <SingleSelect.ItemText>Semi-annually</SingleSelect.ItemText>
                                </SingleSelect.Item>
                                <SingleSelect.Item value={InstallmentFrequency.Annually}>
                                    <SingleSelect.ItemText>Annually</SingleSelect.ItemText>
                                </SingleSelect.Item>
                                <SingleSelect.Item value={InstallmentFrequency.Custom}>
                                    <SingleSelect.ItemText>Custom</SingleSelect.ItemText>
                                </SingleSelect.Item>
                            </SingleSelect.Content>
                        </Form.SingleSelect>
                        <Form.Error name={path('installment_frequency')} />
                    </Form.Container>
                    {installmentFrequency !== InstallmentFrequency.OneTime ? (
                        <>
                            <Form.Container>
                                <Form.Label name={'first_installment_date'}>
                                    First installment date <Form.LabelAsterisk />
                                </Form.Label>
                                <Form.UseFormDatePicker
                                    className="w-full"
                                    control={control}
                                    name={'first_installment_date'}
                                    startInputProps={{ placeholder: '' }}
                                />
                                <Form.Error name={path('first_installment_date')} />
                            </Form.Container>
                            <Form.Container>
                                <Form.Label name={'number_of_installments'}>
                                    Number of installments <Form.LabelAsterisk />
                                </Form.Label>
                                <Form.TextInputRoot
                                    control={control}
                                    name="number_of_installments"
                                    inputRootProps={{
                                        mask: 'whole-number',
                                    }}
                                >
                                    <Form.TextInputForm />
                                </Form.TextInputRoot>
                                <Form.Error name={path('number_of_installments')} />
                            </Form.Container>
                        </>
                    ) : (
                        <Form.Container>
                            <Form.Label name={'fulfilled_at'}>
                                Intended fulfillment date <Form.LabelAsterisk />
                            </Form.Label>
                            <Form.UseFormDatePicker
                                className="w-full"
                                control={control}
                                name={'fulfilled_at'}
                                startInputProps={{ placeholder: '' }}
                            />
                            <Form.Error name={path('fulfilled_at')} />
                        </Form.Container>
                    )}
                    <Form.Container>
                        <Form.Label name={path('type')}>Pledge type</Form.Label>
                        <Form.SingleSelect control={control} name="type">
                            <SingleSelect.Trigger aria-label="Pledge type" />
                            <SingleSelect.Content>
                                <SingleSelect.Item value={PledgeType.Donation}>
                                    <SingleSelect.ItemText>Donation</SingleSelect.ItemText>
                                </SingleSelect.Item>
                                <SingleSelect.Item value={PledgeType.Sponsorship}>
                                    <SingleSelect.ItemText>Sponsorship</SingleSelect.ItemText>
                                </SingleSelect.Item>
                                <SingleSelect.Item value={PledgeType.Match}>
                                    <SingleSelect.ItemText>Match</SingleSelect.ItemText>
                                </SingleSelect.Item>
                                <SingleSelect.Item value={PledgeType.Payroll}>
                                    <SingleSelect.ItemText>Payroll</SingleSelect.ItemText>
                                </SingleSelect.Item>
                                <SingleSelect.Item value={PledgeType.Other}>
                                    <SingleSelect.ItemText>Other</SingleSelect.ItemText>
                                </SingleSelect.Item>
                            </SingleSelect.Content>
                        </Form.SingleSelect>
                        <Form.Error name={path('type')} />
                    </Form.Container>
                    <Form.Container>
                        <Form.Label name={'fund'}>Fund</Form.Label>
                        <Select.UseFormSearchableSelect
                            clearable
                            control={control}
                            name={'fund'}
                            multi={false}
                        >
                            {!funds.length && (
                                <div className="p-4 text-gray-600">No funds found.</div>
                            )}
                            {funds.map(fund => (
                                <Select.ListItem
                                    key={fund.id}
                                    value={{
                                        id: fund.id.toString(),
                                        label: fund.name,
                                    }}
                                >
                                    {fund.name}
                                </Select.ListItem>
                            ))}
                        </Select.UseFormSearchableSelect>
                        <Form.Error name={path('fund')} />
                    </Form.Container>
                </div>
                <Form.Container>
                    <Form.Label name={'campaign'}>
                        Associated campaign <Form.LabelAsterisk />
                    </Form.Label>
                    <SelectCampaign />
                    <Form.Error name={path('campaign')} />
                </Form.Container>
                <Form.Container>
                    <Form.Label name="memo">Memo</Form.Label>
                    <Form.UseFormTextArea
                        control={control}
                        name="memo"
                        maxLength={600}
                        placeholder="Include a personalized message to your donor"
                        rows={9}
                    />
                    <span className="text-xs text-gray-500">
                        {memo?.length || 0}/600 characters
                    </span>
                    <Form.Error name={path('memo')} />
                </Form.Container>
                <Form.Container>
                    <Form.Label name="internal_note">Internal note</Form.Label>
                    <Form.UseFormTextArea
                        control={control}
                        name="internal_note"
                        maxLength={600}
                        placeholder="Add a note for internal use."
                        rows={9}
                    />
                    <span className="text-xs text-gray-500">
                        {internalNote?.length || 0}/600 characters
                    </span>
                    <Form.Error name={path('internal_note')} />
                </Form.Container>
            </div>
        </div>
    );
};

export { PledgeBasicInfoForm };
